import { useState } from "react";
import { CircleSlash, Plus } from "lucide-react";
import { PageShell } from "./page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, ErrorState } from "@/components/common/empty-state";
import { FieldRow } from "./courses";
import { useRules, useSaveRule } from "@/lib/queries";
import { useToast } from "@/components/ui/toast";
import type { PlacementRule } from "@/lib/types";

const COMPARATORS = [">=", ">", "=", "<", "<="];
const TRACKS = ["sat", "presat"];
const SUBJECTS = ["Math", "English"];

export function RulesPage() {
  const { data, isLoading, isError, refetch } = useRules();
  const [open, setOpen] = useState(false);

  return (
    <PageShell
      title="Placement rules"
      description="Conditions the bot uses to decide a student's track and confidence. Order by priority."
      actions={
        <Button variant="primary" size="md" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" />
          New rule
        </Button>
      }
    >
      <div className="rounded-lg border border-line bg-surface">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState title="Couldn't load rules" onRetry={() => void refetch()} />
        ) : !Array.isArray(data) || data.length === 0 ? (
          <EmptyState
            icon={CircleSlash}
            title="No placement rules yet"
            description="Add a rule like 'SAT Math score >= 25 → track: sat (confidence 0.7)'."
            action={
              <Button variant="primary" onClick={() => setOpen(true)}>
                <Plus className="h-4 w-4" />
                New rule
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead className="text-right">Priority</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data
                .slice()
                .sort((a, b) => Number(a.priority ?? 0) - Number(b.priority ?? 0))
                .map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <div className="font-medium text-ink">{rule.name}</div>
                      {rule.notes && (
                        <div className="text-xs text-ink-faint truncate">{rule.notes}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge tone="outline">{rule.subject}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-ink-muted">
                      {rule.evidence_type} {rule.comparator} {rule.threshold}
                    </TableCell>
                    <TableCell>
                      <Badge tone="accent">{rule.outcome_track}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {Number(rule.confidence ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{rule.priority}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </div>

      {open && <RuleDialog onClose={() => setOpen(false)} />}
    </PageShell>
  );
}

function RuleDialog({ onClose }: { onClose: () => void }) {
  const save = useSaveRule();
  const toast = useToast();
  const [form, setForm] = useState<Partial<PlacementRule>>({
    name: "",
    subject: "Math",
    evidence_type: "",
    comparator: ">=",
    threshold: 0,
    outcome_track: "sat",
    confidence: 0.7,
    priority: 100,
    notes: "",
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync({
        ...form,
        threshold: Number(form.threshold),
        confidence: Number(form.confidence),
        priority: Number(form.priority),
      });
      toast.success("Rule saved");
      onClose();
    } catch (err) {
      toast.error("Save failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New placement rule</DialogTitle>
          <DialogDescription>
            Lower priority numbers evaluate first.
          </DialogDescription>
        </DialogHeader>

        <form id="rule-form" onSubmit={onSubmit} className="space-y-5">
          <FieldRow label="Rule name" id="name">
            <Input
              id="name"
              value={form.name ?? ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </FieldRow>
          <div className="grid gap-3 sm:grid-cols-2">
            <FieldRow label="Subject" id="subject">
              <select
                id="subject"
                value={form.subject ?? "Math"}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="flex h-9 w-full rounded-md border border-line-strong bg-surface px-3 text-base"
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </FieldRow>
            <FieldRow label="Evidence type" id="evidence_type" hint="e.g. sat_math, ielts, cefr">
              <Input
                id="evidence_type"
                value={form.evidence_type ?? ""}
                onChange={(e) => setForm({ ...form, evidence_type: e.target.value })}
                required
              />
            </FieldRow>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <FieldRow label="Comparator" id="comparator">
              <select
                id="comparator"
                value={form.comparator ?? ">="}
                onChange={(e) => setForm({ ...form, comparator: e.target.value })}
                className="flex h-9 w-full rounded-md border border-line-strong bg-surface px-3 text-base"
              >
                {COMPARATORS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </FieldRow>
            <FieldRow label="Threshold" id="threshold">
              <Input
                id="threshold"
                type="number"
                step="0.1"
                value={form.threshold ?? 0}
                onChange={(e) => setForm({ ...form, threshold: Number(e.target.value) })}
                required
              />
            </FieldRow>
            <FieldRow label="Track" id="outcome_track">
              <select
                id="outcome_track"
                value={form.outcome_track ?? "sat"}
                onChange={(e) => setForm({ ...form, outcome_track: e.target.value })}
                className="flex h-9 w-full rounded-md border border-line-strong bg-surface px-3 text-base"
              >
                {TRACKS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </FieldRow>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FieldRow label="Confidence" id="confidence" hint="0 - 1">
              <Input
                id="confidence"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={form.confidence ?? 0.7}
                onChange={(e) => setForm({ ...form, confidence: Number(e.target.value) })}
              />
            </FieldRow>
            <FieldRow label="Priority" id="priority" hint="Lower = checked first">
              <Input
                id="priority"
                type="number"
                value={form.priority ?? 100}
                onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
              />
            </FieldRow>
          </div>
          <FieldRow label="Notes" id="notes">
            <Textarea
              id="notes"
              rows={3}
              value={form.notes ?? ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </FieldRow>
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="rule-form" variant="primary" disabled={save.isPending}>
            Save rule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
